import { useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { getReactionsSummary, getReactionsQueryInfo } from '../utils/data';
import { getBooleanLocationTypes } from '../utils/utils';
import type { ReactionsContentQuery_content_nodes_contentReactionsSummary_reactionsSummaryForEmoji as ReactionsSummaryForEmoji } from '../graphql/__types__/ReactionsContentQuery';
import type {
	ReactionLocation,
	Reaction,
	ReactionsData,
	QueryData,
	QueryVariables,
	ReactedUsersMap,
} from '../types';

type ReactionsDataValue = {
	reactions: Reaction[];
	totalCount: number;
	ari: string;
	containerAri: string;
};

type ReactionsDataReturnType = {
	data: ReactionsDataValue;
	loading?: boolean;
	error?: ApolloError;
};

type ReactionsDataFnProps = (input: {
	location: ReactionLocation;
	contentId: string;
	containerId: string;
	reactedUsersMap: ReactedUsersMap;
	useNetworkOnly: boolean;
	inheritedReactionsData?: ReactionsData;
}) => ReactionsDataReturnType;

export const useReactionsData: ReactionsDataFnProps = ({
	location,
	contentId,
	containerId,
	reactedUsersMap,
	useNetworkOnly,
	inheritedReactionsData,
}) => {
	const { isComment } = getBooleanLocationTypes(location);

	const { query, queryVariables } = getReactionsQueryInfo(isComment, contentId, containerId);

	const { data, loading, error } = useQuery<QueryData, QueryVariables>(query, {
		variables: queryVariables,
		fetchPolicy: isComment && useNetworkOnly ? 'network-only' : 'cache-and-network',
		skip: inheritedReactionsData !== undefined, // null is a valid case meaning an error ocurred higher up
	});

	return useMemo(() => {
		if (loading || error || (!data && !inheritedReactionsData)) {
			return {
				data: {
					reactions: [],
					totalCount: 0,
					ari: '',
					containerAri: '',
				},
				loading,
				error,
			};
		}

		const reactionsSummary = inheritedReactionsData || getReactionsSummary(data);

		const totalCount = reactionsSummary?.reactionsCount || 0;
		const containerAri = reactionsSummary?.containerAri || '';
		const ari = reactionsSummary?.ari || '';
		const reactionNodes = reactionsSummary?.reactionsSummaryForEmoji || [];

		// We need to filter out all the "null" reactions
		const filteredNodes = reactionNodes?.filter(
			(reaction) => reaction !== null,
		) as ReactionsSummaryForEmoji[];

		// Transform reaction nodes to the correct shape for the AkReactions component
		const shapedReactions = filteredNodes.map(({ emojiId, count, reacted }) => {
			const users = reactedUsersMap[emojiId] || [];

			return {
				ari,
				containerAri,
				emojiId,
				count,
				reacted,
				users,
			};
		});

		return {
			data: {
				totalCount,
				reactions: shapedReactions,
				ari,
				containerAri,
			},
		};
	}, [data, inheritedReactionsData, reactedUsersMap, loading, error]);
};
